import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';

import Button from '../../components/ui/Button';
import OrderFilters from './components/OrderFilters';
import FahrtennachweisTable from './components/FahrtennachweisTable';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import OrderSummaryPanel from './components/OrderSummaryPanel';
import NewOrderModal from './components/NewOrderModal';
import AddFahrtModal from './components/AddFahrtModal';

import { orderService } from '../../services/orderService';
import { fahrtenService } from '../../services/fahrtenService';
import { useAuth } from '../../contexts/AuthContext';
import { pdfTemplateService } from '../../services/pdfTemplateService';

const OrderManagementDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [showAddFahrtModal, setShowAddFahrtModal] = useState(false);

  const [fahrten, setFahrten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dealership: '',
    technician: '',
    priority: '',
    dateFrom: '',
    dateTo: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  // License plate state
  const [licensePlateSuffix, setLicensePlateSuffix] = useState('');


  // Load fahrten from database
  const loadFahrten = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Convert frontend filters to backend format for fahrten
      const backendFilters = {};
      if (filters.status) backendFilters.status = filters.status;
      if (filters.dateFrom) backendFilters.dateFrom = filters.dateFrom;
      if (filters.dateTo) backendFilters.dateTo = filters.dateTo;

      const { data, error: fetchError } = await fahrtenService.getFahrten(user.id, backendFilters);
      
      if (fetchError) {
        throw fetchError;
      }

      setFahrten(data || []);
    } catch (err) {
      console.error('Error loading fahrten:', err);
      setError(err.message || 'Failed to load fahrten');
      setFahrten([]);
    } finally {
      setLoading(false);
    }
  };

  // Load fahrten on component mount and when filters change
  useEffect(() => {
    loadFahrten();
  }, [filters]);

  // Handle navigation from VIN scanner with new fahrt
  useEffect(() => {
    if (location.state?.newFahrt) {
      console.log('🔍 New fahrt detected from VIN scanner, will reset to page 1');
      // Set flag to reset to page 1 when fahrten are loaded
      setShouldResetToPage1(true);
      // Clear the navigation state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Track if we should reset to page 1 (only when coming from VIN scanner)
  const [shouldResetToPage1, setShouldResetToPage1] = useState(false);
  
  // Reset to page 1 when fahrten are loaded and we should reset
  useEffect(() => {
    if (shouldResetToPage1 && fahrten.length > 0) {
      console.log('🔍 Resetting to page 1 for new fahrt');
      setCurrentPage(1);
      setShouldResetToPage1(false);
    }
  }, [fahrten.length, shouldResetToPage1]);

  // Summary data based on loaded fahrten
  const summaryData = {
    newOrders: fahrten?.filter(f => f?.status === 'pending')?.length || 0,
    inProgress: fahrten?.filter(f => f?.status === 'in_progress')?.length || 0,
    completedToday: fahrten?.filter(f => f?.status === 'completed' && 
      new Date(f.updated_at)?.toDateString() === new Date()?.toDateString())?.length || 0,
    overdue: fahrten?.filter(f => {
      const createdDate = new Date(f.created_at);
      const daysDiff = (new Date() - createdDate) / (1000 * 60 * 60 * 24);
      return daysDiff > 3 && !['completed', 'cancelled']?.includes(f?.status);
    })?.length || 0
  };

  // Notification counts for header
  const notificationCounts = {
    orders: summaryData?.newOrders + summaryData?.overdue,
    technicians: 2
  };

  // Filter fahrten based on current filters (client-side filtering for additional features)
  const filteredFahrten = fahrten?.filter(fahrt => {
    if (filters?.search && !fahrt?.vin?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !fahrt?.fahrer_name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !fahrt?.startziel?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !fahrt?.endziel?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.status && fahrt?.status !== filters?.status) return false;
    if (filters?.dateFrom) {
      const fahrtDate = new Date(fahrt.fahrt_datum);
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (fahrtDate < fromDate) return false;
    }
    if (filters?.dateTo) {
      const fahrtDate = new Date(fahrt.fahrt_datum);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (fahrtDate > toDate) return false;
    }
    return true;
  });

  // Notebook-style pagination: Page 1 is always the current working page
  // When Page 1 fills up (8 fahrten), it gets pushed to Page 2, and a new Page 1 is created
  const totalFahrten = filteredFahrten?.length || 0;
  let totalPages, paginatedFahrten;
  
  if (totalFahrten === 0) {
    totalPages = 1;
    paginatedFahrten = [];
  } else {
    // Calculate total pages: each time we have 8 fahrten, we get a new page
    totalPages = Math.ceil(totalFahrten / itemsPerPage);
    
    if (currentPage === 1) {
      // Page 1: Current working page (newest fahrten)
      // Take the last (newest) fahrten, up to 8
      const fahrtenInPage1 = totalFahrten % itemsPerPage === 0 ? itemsPerPage : totalFahrten % itemsPerPage;
      const startIndex = totalFahrten - fahrtenInPage1;
      const endIndex = totalFahrten;
      paginatedFahrten = filteredFahrten?.slice(startIndex, endIndex);
    } else {
      // Page 2+: Full pages (8 fahrten each) - these are the "pushed" pages
      // We need to work backwards from the newest fahrten
      const fahrtenInPage1 = totalFahrten % itemsPerPage === 0 ? itemsPerPage : totalFahrten % itemsPerPage;
      const pageNumber = currentPage - 1; // Page 2 = first pushed page, Page 3 = second pushed page, etc.
      
      // Calculate the start index for this pushed page
      // Each pushed page has 8 fahrten, and we work backwards from the newest
      const startIndex = totalFahrten - fahrtenInPage1 - (pageNumber * itemsPerPage);
      const endIndex = startIndex + itemsPerPage;
      paginatedFahrten = filteredFahrten?.slice(startIndex, endIndex);
    }
  }
  

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Pagination control functions
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleOrderSelect = (orderId, isSelected) => {
    if (isSelected) {
      setSelectedOrders(prev => [...prev, orderId]);
    } else {
      setSelectedOrders(prev => prev?.filter(id => id !== orderId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedOrders(filteredOrders?.map(order => order?.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      // Prepare update data
      const updateData = { status: newStatus };
      
      // If status is being changed to 'pending' (Neu) or 'review' (In Prüfung), unassign technician
      if (newStatus === 'pending' || newStatus === 'review') {
        updateData.assigned_technician_id = null;
      }

      const result = await orderService.updateOrder(orderId, updateData, user.id);
      
      if (result.error) {
        console.error('Error updating order status:', result.error);
        alert('Fehler beim Aktualisieren des Fahrtstatus: ' + result.error.message);
      } else {
        
        // Reload fahrten to reflect the change
        await loadFahrten();
        
        // Show success message with technician unassignment info if applicable
        if (newStatus === 'pending' || newStatus === 'review') {
          alert(`Status erfolgreich zu "${newStatus === 'pending' ? 'Neu' : 'In Prüfung'}" geändert und Techniker abgemeldet!`);
        }
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Fehler beim Aktualisieren des Fahrtstatus: ' + error.message);
    }
  };

  const handleBulkAction = async (action, orderIds = selectedOrders) => {
    console.log('Bulk action:', action, 'for orders:', orderIds);
    console.log('Current user:', user);
    
    if (!user?.id) {
      alert('User not authenticated');
      return;
    }

    try {
      switch (action) {
        case 'assign':
          // Navigate to technician assignment
          navigate('/technician-assignment');
          break;
        case 'status-pending':
          console.log('Updating orders to pending status...');
          // Update orders to pending status and unassign technicians
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to status: pending');
            const result = await orderService.updateOrder(orderId, { 
              status: 'pending',
              assigned_technician_id: null 
            }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadFahrten(); // Reload fahrten
          alert(`Status erfolgreich zu "Neu" geändert und Techniker abgemeldet für ${orderIds.length} Fahrt${orderIds.length !== 1 ? 'en' : ''}`);
          break;
        case 'status-review':
          console.log('Updating orders to review status...');
          // Update orders to review status and unassign technicians
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to status: review');
            const result = await orderService.updateOrder(orderId, { 
              status: 'review',
              assigned_technician_id: null 
            }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadFahrten(); // Reload fahrten
          alert(`Status erfolgreich zu "In Prüfung" geändert und Techniker abgemeldet für ${orderIds.length} Fahrt${orderIds.length !== 1 ? 'en' : ''}`);
          break;
        case 'status-assigned':
          console.log('Updating orders to assigned status...');
          // Update orders to assigned status - but only if they have a technician assigned
          let assignedCount = 0;
          let errorCount = 0;
          
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to status: assigned');
            const result = await orderService.updateOrder(orderId, { status: 'assigned' }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
              errorCount++;
            } else {
              assignedCount++;
            }
          }
          
          await loadFahrten(); // Reload fahrten
          
          if (errorCount > 0) {
            alert(`Status konnte nicht für ${errorCount} Fahrt${errorCount !== 1 ? 'en' : ''} geändert werden. Fahrten müssen zuerst einem Techniker zugewiesen werden.`);
          } else {
            alert(`Status erfolgreich zu "Zugewiesen" geändert für ${assignedCount} Fahrt${assignedCount !== 1 ? 'en' : ''}`);
          }
          break;
        case 'status-progress':
          console.log('Updating orders to in progress status...');
          // Update orders to in progress status - but only if they have a technician assigned
          let progressCount = 0;
          let progressErrorCount = 0;
          
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to status: in_progress');
            const result = await orderService.updateOrder(orderId, { status: 'in_progress' }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
              progressErrorCount++;
            } else {
              progressCount++;
            }
          }
          
          await loadFahrten(); // Reload fahrten
          
          if (progressErrorCount > 0) {
            alert(`Status konnte nicht für ${progressErrorCount} Fahrt${progressErrorCount !== 1 ? 'en' : ''} geändert werden. Fahrten müssen zuerst einem Techniker zugewiesen werden.`);
          } else {
            alert(`Status erfolgreich zu "In Bearbeitung" geändert für ${progressCount} Fahrt${progressCount !== 1 ? 'en' : ''}`);
          }
          break;
        case 'status-completed':
          console.log('Updating orders to completed status...');
          let completedCount = 0;
          let completedErrorCount = 0;
          
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to status: completed');
            try {
              const result = await orderService.updateOrder(orderId, { status: 'completed' }, user.id);
              console.log('Update result:', result);
              if (result.error) {
                console.error('Error updating order to completed:', result.error);
                completedErrorCount++;
              } else {
                completedCount++;
              }
            } catch (error) {
              console.error('Exception updating order to completed:', error);
              completedErrorCount++;
            }
          }
          
          await loadFahrten(); // Reload fahrten
          
          if (completedErrorCount > 0) {
            alert(`Status konnte nicht für ${completedErrorCount} Fahrt${completedErrorCount !== 1 ? 'en' : ''} zu "Abgeschlossen" geändert werden. Prüfen Sie die Konsole für Details.`);
          } else {
            alert(`Status erfolgreich zu "Abgeschlossen" geändert für ${completedCount} Fahrt${completedCount !== 1 ? 'en' : ''}`);
          }
          break;
        case 'status-cancelled':
          console.log('Updating orders to cancelled status...');
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to status: cancelled');
            const result = await orderService.updateOrder(orderId, { status: 'cancelled' }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadFahrten(); // Reload fahrten
          alert(`Status erfolgreich zu "Storniert" geändert für ${orderIds.length} Fahrt${orderIds.length !== 1 ? 'en' : ''}`);
          break;
        case 'priority-low':
          console.log('Updating orders to low priority...');
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to priority: low');
            const result = await orderService.updateOrder(orderId, { priority: 'low' }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadFahrten(); // Reload fahrten
          alert(`Priorität erfolgreich zu "Niedrig" geändert für ${orderIds.length} Fahrt${orderIds.length !== 1 ? 'en' : ''}`);
          break;
        case 'priority-normal':
          console.log('Updating orders to normal priority...');
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to priority: normal');
            const result = await orderService.updateOrder(orderId, { priority: 'normal' }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadFahrten(); // Reload fahrten
          alert(`Priorität erfolgreich zu "Normal" geändert für ${orderIds.length} Fahrt${orderIds.length !== 1 ? 'en' : ''}`);
          break;
        case 'priority-high':
          console.log('Updating orders to high priority...');
          // Update orders to high priority
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to priority: high');
            const result = await orderService.updateOrder(orderId, { priority: 'high' }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadFahrten(); // Reload fahrten
          alert(`Priorität erfolgreich zu "Hoch" geändert für ${orderIds.length} Fahrt${orderIds.length !== 1 ? 'en' : ''}`);
          break;
        case 'priority-urgent':
          console.log('Updating orders to urgent priority...');
          // Update orders to urgent priority
          for (const orderId of orderIds) {
            console.log('Updating order:', orderId, 'to priority: urgent');
            const result = await orderService.updateOrder(orderId, { priority: 'urgent' }, user.id);
            console.log('Update result:', result);
            if (result.error) {
              console.error('Error updating order:', result.error);
            }
          }
          await loadFahrten(); // Reload fahrten
          alert(`Priorität erfolgreich zu "Dringend" geändert für ${orderIds.length} Fahrt${orderIds.length !== 1 ? 'en' : ''}`);
          break;
        case 'delete':
          if (window.confirm(`${orderIds?.length} Fahrt${orderIds?.length !== 1 ? 'en' : ''} wirklich löschen?`)) {
            let deleteCount = 0;
            let errorCount = 0;
            
            for (const orderId of orderIds) {
              try {
                const result = await orderService.deleteOrder(orderId, user.id);
                if (result.error) {
                  console.error('Error deleting order:', result.error);
                  errorCount++;
                } else {
                  deleteCount++;
                }
              } catch (error) {
                console.error('Error deleting order:', error);
                errorCount++;
              }
            }
            
            await loadFahrten(); // Reload fahrten
            
            if (errorCount > 0) {
              alert(`${deleteCount} Fahrt${deleteCount !== 1 ? 'en' : ''} erfolgreich gelöscht. ${errorCount} Fahrt${errorCount !== 1 ? 'en' : ''} konnten nicht gelöscht werden.`);
            } else {
              alert(`${deleteCount} Fahrt${deleteCount !== 1 ? 'en' : ''} erfolgreich gelöscht!`);
            }
          }
          break;
        default:
          console.log('Unknown action:', action);
          break;
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Error performing bulk action: ' + error.message);
    }
    
    setSelectedOrders([]);
  };

  const handleExport = (format) => {
    console.log('Exporting orders in format:', format);
    // Mock export functionality
    const exportData = selectedOrders?.length > 0 
      ? orders?.filter(order => selectedOrders?.includes(order?.id))
      : filteredOrders;
    
    alert(`${exportData?.length} Fahrten werden als ${format?.toUpperCase()} exportiert.`);
  };

  const handleCreateOrder = async (orderData) => {
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Create fahrt in database
      const { data: newOrder, error: createError } = await orderService.createOrder(orderData, user.id);
      
      if (createError) {
        throw createError;
      }

      // Reload fahrten to show the new fahrt
      await loadFahrten();
      
      console.log('New order created:', newOrder);
      alert('Fahrt erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error creating fahrt: ' + error.message);
    }
  };

  const handleCreateFahrt = async (fahrtData) => {
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Create fahrt in database
      const { data: newFahrt, error: createError } = await fahrtenService.createFahrt(fahrtData, user.id);
      
      if (createError) {
        throw createError;
      }

      // Reload fahrten to show the new fahrt
      await loadFahrten();
      
      console.log('New fahrt created:', newFahrt);
      alert('Fahrt erfolgreich hinzugefügt!');
    } catch (error) {
      console.error('Error creating fahrt:', error);
      alert('Fehler beim Hinzufügen der Fahrt: ' + error.message);
    }
  };



  // Handle fahrt deletion
  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Möchten Sie die Fahrt "${order.order_number}" wirklich löschen?`)) {
      return;
    }

    try {
      const { error } = await orderService.deleteOrder(order.id, user.id);
      
      if (error) {
        throw error;
      }

      // Refresh fahrten list
      await loadFahrten();
      
      // Show success message
      console.log('Order deleted successfully');
      alert('Fahrt erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Fehler beim Löschen der Fahrt: ' + error.message);
    }
  };



  // Handle fahrt editing
  const handleEditOrder = async (orderId, updatedData) => {
    console.log('Order edited:', orderId, updatedData);
    // Refresh fahrten to show the updated data
    await loadFahrten();
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dealership: '',
      technician: '',
      priority: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleRefreshSummary = async () => {
    console.log('Refreshing summary data...');
    await loadFahrten();
  };

  // Handle fahrt updates and deletions
  const handleFahrtUpdated = (updatedFahrt) => {
    setFahrten(prevFahrten => 
      prevFahrten.map(fahrt => 
        fahrt.id === updatedFahrt.id ? updatedFahrt : fahrt
      )
    );
  };

  const handleFahrtDeleted = (fahrtId) => {
    setFahrten(prevFahrten => 
      prevFahrten.filter(fahrt => fahrt.id !== fahrtId)
    );
    
    // Reset to first page after deletion to ensure we don't end up on an empty page
    const remainingFahrten = fahrten.filter(fahrt => fahrt.id !== fahrtId);
    const newTotalPages = Math.ceil(remainingFahrten.length / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }
  };

  // Generate PDF for current page fahrten using template
  const generateFahrtenPDF = async () => {
    if (paginatedFahrten.length === 0) {
      alert('Keine Fahrten auf der aktuellen Seite zum Erstellen einer PDF');
      return;
    }

    try {
      console.log(`🔍 Generating PDF for current page (${currentPage}) with ${paginatedFahrten.length} fahrten`);
      const licensePlate = `${licensePlateSuffix}`;
      await pdfTemplateService.generatePDF(paginatedFahrten, { licensePlate });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Fehler beim Erstellen der PDF. Bitte versuchen Sie es erneut.');
    }
  };


  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" notificationCounts={notificationCounts} />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
          notificationCounts={notificationCounts}
        />
      </div>
      <main className={`transition-all duration-200 ease-out ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      } pt-16`}>
        <div className="flex">
          {/* Main Content */}
          <div className="flex-1 p-6 lg:pr-0">
            <div className="max-w-none">
              <Breadcrumb />
              
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary mb-2">Fahrtennachweis (Rote Kennzeichen)</h1>
                  <p className="text-text-secondary">
                    Übersicht aller Fahrten in Tabellenform
                  </p>
                </div>
                
                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/vin-scanner')}
                    iconName="Scan"
                    iconPosition="left"
                  >
                    VIN Scanner
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/ende-zeit-scanner')}
                    iconName="Clock"
                    iconPosition="left"
                  >
                    Ende-Zeit Scanner
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => setShowAddFahrtModal(true)}
                    iconName="Plus"
                    iconPosition="left"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Fahrt hinzufügen
                  </Button>
                  {paginatedFahrten.length > 0 && (
                    <Button
                      variant="default"
                      onClick={generateFahrtenPDF}
                      iconName="FileText"
                      iconPosition="left"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      PDF erstellen (Seite {currentPage}, {paginatedFahrten.length} Fahrten)
                    </Button>
                  )}
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">Error: {error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadFahrten}
                    className="mt-2"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {/* <OrderFilters
                filters={filters}
                onFiltersChange={setFilters}
                onClearFilters={handleClearFilters}
              /> */}

              <BulkActionsToolbar
                selectedCount={selectedOrders?.length}
                onBulkAction={handleBulkAction}
                onExport={handleExport}
              />

              <FahrtennachweisTable
                fahrten={paginatedFahrten}
                loading={loading}
                kennzeichenPrefix=""
                licensePlateSuffix={licensePlateSuffix}
                onLicensePlateChange={setLicensePlateSuffix}
                onFahrtUpdated={handleFahrtUpdated}
                onFahrtDeleted={handleFahrtDeleted}
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredFahrten?.length}
                onPageChange={handlePageChange}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
              />
            </div>
          </div>

          {/* Right Sidebar - Summary Panel */}
          <div className="hidden xl:block w-10 ">
            {/* <OrderSummaryPanel
              summaryData={summaryData}
              onRefresh={handleRefreshSummary}
            /> */}
          </div>
        </div>
      </main>
      <QuickActionButton userRole="admin" />
      <NewOrderModal
        isOpen={showNewOrderModal}
        onClose={() => setShowNewOrderModal(false)}
        onCreateOrder={handleCreateOrder}
      />
      <AddFahrtModal
        isOpen={showAddFahrtModal}
        onClose={() => setShowAddFahrtModal(false)}
        onCreateFahrt={handleCreateFahrt}
      />

    </div>
  );
};

export default OrderManagementDashboard;